import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Hotel, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import CustomerHeader from "@/components/customer/CustomerHeader";
import { BrandHeader, BrandFooter, useBrand } from "@/components/customer/Brand";
import PaymentMethods from "@/components/customer/PaymentMethods";
import { PlanPerks } from "@/components/customer/RatePlanRow";
import { useShopper } from "@/components/customer/ShopperProvider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { http, idr, fmtDate, useStay, stayQuery, errMsg, ctxParams } from "@/lib/api";
import { payBooking } from "@/lib/pay";
import Img from "@/components/Img";
import { useT, useLoc } from "@/lib/i18n";

export const Summary = ({ p, room, rp, q, stay }) => { const t = useT(); const loc = useLoc(); return (
  <aside className="h-fit overflow-hidden rounded-2xl border border-slate-200 bg-white lg:sticky lg:top-20" data-testid="checkout-summary">
    <Img src={room.images[0]} sizes="400px" alt={room.name} className="h-36 w-full object-cover" />
    <div className="space-y-3 p-5 text-sm">
      <div><p className="font-heading text-lg font-bold text-slate-900">{p.name}</p><p className="text-slate-500">{loc(room, "name")} · {loc(rp, "name")}</p></div>
      <PlanPerks rp={rp} />
      <div className="grid grid-cols-2 gap-2 rounded-xl bg-slate-50 p-3">
        <div><p className="text-xs text-slate-400">Check-in</p><p className="font-semibold">{fmtDate(stay.check_in)}</p></div>
        <div><p className="text-xs text-slate-400">Check-out</p><p className="font-semibold">{fmtDate(stay.check_out)}</p></div>
      </div>
      <p className="text-slate-600">{t("{n} malam · {r} kamar · {g} tamu", { n: q.nights, r: stay.rooms, g: stay.guests })}</p>
      <div className="space-y-1.5 border-t border-slate-100 pt-3" data-testid="price-breakdown">
        <div className="flex justify-between"><span>{t("Harga kamar")}</span><span>{idr(q.base_subtotal)}</span></div>
        {q.promotions.map((pr) => (
          <div key={pr.id} className="flex justify-between text-[#00A698]"><span>{t(pr.label)} (-{pr.discount_percent}%)</span><span>-{idr(pr.saved)}</span></div>
        ))}
        {q.tax > 0 && <div className="flex justify-between text-slate-500"><span>{t("Pajak & biaya layanan")} ({q.tax_percent}%)</span><span>{idr(q.tax)}</span></div>}
        <div className="flex justify-between border-t border-slate-100 pt-2 font-heading text-lg font-extrabold">
          <span>Total</span><span data-testid="checkout-total" className="text-[#E1231A]">{idr(q.grand_total)}</span>
        </div>
      </div>
    </div>
  </aside>
); };

export default function Checkout() {
  const stay = useStay();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { member, country } = useShopper();
  const t = useT();
  const [pid, rid, planId] = [sp.get("property"), sp.get("room"), sp.get("plan")];
  const brand = useBrand(pid);
  const promo = sessionStorage.getItem("promo_code") || "";
  const [data, setData] = useState(null);
  const [methods, setMethods] = useState([]);
  const [method, setMethod] = useState("");
  const [g, setG] = useState({ name: "", email: "", phone: "", special_request: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get(`/public/properties/${pid}?${stayQuery(stay)}`, { params: ctxParams(country, promo) }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [pid, country, member?.id]); // eslint-disable-line
  useEffect(() => {
    http.get("/payments/methods").then((r) => { setMethods(r.data); setMethod(r.data.find((m) => m.active)?.id || ""); });
  }, []);
  useEffect(() => { if (member) setG((o) => ({ ...o, name: o.name || member.name, email: o.email || member.email })); }, [member]);

  const item = data?.rooms.find((x) => x.room.id === rid);
  const plan = item?.plans.find((x) => x.rate_plan.id === planId);
  const pah = plan?.rate_plan.payment_type === "pay_at_hotel";

  const submit = async (e) => {
    e.preventDefault();
    if (!pah && !method) return toast.error(t("Pilih metode pembayaran"));
    setBusy(true);
    try {
      const { data: b } = await http.post("/public/bookings", {
        property_id: pid, room_type_id: rid, rate_plan_id: planId, check_in: stay.check_in, check_out: stay.check_out,
        rooms: stay.rooms, guests: stay.guests, guest: { name: g.name, email: g.email, phone: g.phone },
        special_request: g.special_request, branded: !!brand, ...ctxParams(country, promo),
      });
      const goto = () => nav(`/booking/${b.code}?email=${encodeURIComponent(b.guest.email)}`);
      if (pah) return goto();
      await payBooking({ code: b.code, email: b.guest.email, method, onDone: goto });
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  const set = (k) => (e) => setG({ ...g, [k]: e.target.value });
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <BrandHeader brand={brand} />
      <main className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-4 py-8 sm:px-6 lg:grid-cols-3">
        <form onSubmit={submit} className="flex flex-col gap-6 lg:col-span-2" data-testid="checkout-form">
          <section className="rounded-2xl border border-slate-200 bg-white p-6">
            <h1 className="font-heading text-2xl font-bold text-slate-900">{t("Data tamu")}</h1>
            <p className="mt-1 text-sm text-slate-500">{t("Konfirmasi booking akan dikaitkan dengan email ini.")}</p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2"><Label htmlFor="gname">{t("Nama lengkap")}</Label>
                <Input id="gname" data-testid="guest-name-input" required value={g.name} onChange={set("name")} className="mt-1.5" /></div>
              <div><Label htmlFor="gemail">Email</Label>
                <Input id="gemail" data-testid="guest-email-input" type="email" required value={g.email} onChange={set("email")} className="mt-1.5" /></div>
              <div><Label htmlFor="gphone">{t("No. telepon")}</Label>
                <Input id="gphone" data-testid="guest-phone-input" required value={g.phone} onChange={set("phone")} placeholder="+62" className="mt-1.5" /></div>
              <div className="sm:col-span-2"><Label htmlFor="greq">{t("Permintaan khusus (opsional)")}</Label>
                <Textarea id="greq" data-testid="guest-request-input" value={g.special_request} onChange={set("special_request")} className="mt-1.5" /></div>
            </div>
          </section>
          {plan && (pah ? (
            <section className="flex items-start gap-4 rounded-2xl border-2 border-[#00A698] bg-teal-50/50 p-6" data-testid="pay-at-hotel-notice">
              <Hotel className="h-8 w-8 shrink-0 text-[#00A698]" />
              <div><h2 className="font-heading text-lg font-bold text-slate-900">{t("Bayar di hotel")}</h2>
                <p className="text-sm text-slate-600">{t("Tidak perlu bayar sekarang. Booking langsung dikonfirmasi, pembayaran dilakukan saat check-in.")}</p></div>
            </section>
          ) : (
            <section className="rounded-2xl border border-slate-200 bg-white p-6">
              <h2 className="font-heading text-lg font-bold text-slate-900">{t("Metode pembayaran")}</h2>
              <div className="mt-4"><PaymentMethods methods={methods} value={method} onChange={setMethod} /></div>
            </section>
          ))}
          <button type="submit" disabled={busy || !plan?.quote.bookable} data-testid="checkout-submit-button"
            className="flex items-center justify-center gap-2 rounded-xl bg-[#5392F9] py-4 font-heading text-lg font-bold text-white transition-colors hover:bg-[#3B7BE6] disabled:bg-slate-300">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : pah ? <Hotel className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
            {!plan ? t("Memuat...") : pah ? t("Pesan sekarang, bayar di hotel") : t("Bayar {v}", { v: idr(plan.quote.grand_total) })}
          </button>
        </form>
        {plan ? <Summary p={data.property} room={item.room} rp={plan.rate_plan} q={plan.quote} stay={stay} /> : <Skeleton className="h-96 rounded-2xl" />}
      </main>
      {brand && <BrandFooter brand={brand} />}
    </div>
  );
}
